import { Stack, Group } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { Route, Search } from ".."
import { HideableFilterGroup } from "../../../-components/hideable-filter-group"
import { ListTableActionIconGroup } from "../../../-components/list-table-action-icon-group"
import { SearchTextInput } from "../../../-components/search-text-input"
import { SeasonMultiSelect } from "../../../-components/season-multiselect"
import { useUserLocalStorage } from "../../../-components/use-user-local-storage"
import { UserLicenseRestrictedView } from "../../../-components/user-license-restricted-view"
import { TreePillsInput } from "../../../../../-components/tree/tree-pills-input"
import { ProductionDto } from "../../../../../@client/Theasoft_ApiServer_Productions"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { useBreakpoints } from "../../../../../utils/use-breakpoints"
import { ExtendedProductionTable } from "./extended-production-table"
import { MinimalProductionTable } from "./minimal-production-table"
import { ProductionCardList } from "./production-card-list"
import { Page } from "../../../../../-components/Page"
import { useGetMyLicenses } from "../../../-components/use-get-my-licenses"

export function ProductionsPage() {
  const { t, i18n } = useTranslation("app_server")
  const navigate = Route.useNavigate()
  const { data: seasonFilterDto, isPending } = useSuspenseQueryGet(
    "/api/Production/GetDefaultSeason",
    null
  )
  const { data: genreTree } = useSuspenseQueryGet("/api/Genre/GetGenreTree", {
    language: i18n.language,
  })
  const { xs } = useBreakpoints()
  const { data: userLicenses } = useGetMyLicenses()
  const search = Route.useSearch()
  const { view, genreTreeNodes, seasonIds, searchString } = search

  const [storedView, setStoredView] = useUserLocalStorage<"table" | "list">({
    key: "productions-view",
    defaultValue: "list",
  })
  const [storedSeasonIds, setStoredSeasonIds] = useUserLocalStorage<number[]>({
    key: "productions-season-filter",
    defaultValue: seasonFilterDto.defaultValue,
  })
  const [storedGenreTreeNodes, setStoredGenreTreeNodes] = useUserLocalStorage<
    string[]
  >({
    key: "productions-genre-filter",
    defaultValue: [],
  })
  const [showFilters, setShowFilters] = useUserLocalStorage<boolean>({
    key: "productions-show-filters",
    defaultValue: false,
  })
  React.useEffect(() => {
    updateSearch({
      view: search.view ?? storedView ?? "list",
      genreTreeNodes: search.genreTreeNodes || storedGenreTreeNodes || [],
      seasonIds: search.seasonIds ?? storedSeasonIds ?? [],
      searchString: search.searchString ?? "",
    })
  }, [])
  React.useEffect(() => {
    if (
      !userLicenses.some((v) => v.product === "theaDispoWebManager") &&
      view === "table"
    ) {
      setStoredView("list")
      updateSearch({ view: "list" })
    }
  }, [view, userLicenses])
  function navigateToProductionId(production: ProductionDto) {
    navigate({
      to: "/app/art/productions/$id",
      params: { id: production.id.toString() },
      search: { tab: "start", seasonIds: storedSeasonIds },
    })
  }

  function updateSearch(input: Partial<Search>) {
    navigate({
      search: {
        ...search,
        ...input,
      },
      replace: true,
    })
  }

  return (
    <Page
      buttonGroupProps={{
        children: (
          <>
            <HideableFilterGroup.Button
              activeFilters={
                (storedGenreTreeNodes.length ? 1 : 0) +
                (storedSeasonIds.length ? 1 : 0)
              }
              visible={!!showFilters}
              onChange={() => setShowFilters((showFilters) => !showFilters)}
            />
            <UserLicenseRestrictedView licenses={["theaDispoWebManager"]}>
              <ListTableActionIconGroup
                value={storedView}
                onChange={(value) => {
                  setStoredView(value)
                  updateSearch({ view: value })
                }}
              />
            </UserLicenseRestrictedView>
          </>
        ),
      }}
    >
      <Stack h={"100%"}>
        <Group>
          {storedView === "list" ? (
            <SearchTextInput
              maw={400}
              w={"100%"}
              value={searchString}
              onChange={(e) => updateSearch({ searchString: e.target.value })}
            />
          ) : null}
          <HideableFilterGroup visible={!!showFilters}>
            {genreTreeNodes ? (
              <TreePillsInput
                label={t("Genre")}
                w={xs ? "100%" : 400}
                placeholder={t("FilterGenre")}
                tree={genreTree.tree}
                onCheckedStateChange={(v) => {
                  setStoredGenreTreeNodes(v)
                  updateSearch({ genreTreeNodes: v })
                }}
                initialCheckedState={genreTreeNodes}
              />
            ) : null}
            <SeasonMultiSelect
              value={seasonIds}
              data={seasonFilterDto.data}
              onChange={(value) => {
                setStoredSeasonIds(value)
                updateSearch({ seasonIds: value })
              }}
            />
          </HideableFilterGroup>
        </Group>
        {!isPending && view === "list" ? (
          <>
            {xs ? (
              <ProductionCardList
                partialInput={{
                  searchString,
                  genreTreeNodes: genreTreeNodes,
                  seasonIds: seasonIds,
                }}
                onClickItem={navigateToProductionId}
              />
            ) : (
              <MinimalProductionTable
                searchString={searchString}
                seasonIds={seasonIds}
                genreTreeNodes={genreTreeNodes}
                onClickRow={navigateToProductionId}
              />
            )}
          </>
        ) : (
          <UserLicenseRestrictedView licenses={["theaDispoWebManager"]}>
            <ExtendedProductionTable
              partialInput={{
                seasonIds: seasonIds,
                genreTreeNodes: genreTreeNodes,
              }}
              onClickRow={navigateToProductionId}
              searchProps={{
                value: search,
                onChange: (value) => updateSearch(value),
              }}
            />
          </UserLicenseRestrictedView>
        )}
      </Stack>
    </Page>
  )
}
